'use client';

import { memo, useMemo, useRef } from 'react';

import type { LexicalContentRendererProps } from '../../types/lexical.types';
import { useParagraphCommentUi } from '../../hooks/use-paragraph-comment-ui';
import { useSelectionShareBubble } from '../../hooks/use-selection-share-bubble';
import { parseLexicalNodes } from '../../utils/parse-lexical-nodes.util';
import { renderLexicalNode } from '../../utils/render-lexical-node.util';
import { ParagraphCommentUiContext } from './paragraph-comment-ui-context';
import { SelectionShareBubble } from './selection-share-bubble';

export type { LexicalContentRendererProps } from '../../types/lexical.types';

export const LexicalContentRenderer = memo(
  ({
    content,
    textColor = '#111827',
    inheritTypography = false,
    selectedParagraphId,
    getParagraphCommentCount,
    onCommentIconClick,
  }: LexicalContentRendererProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { paragraphCommentUi } = useParagraphCommentUi({
      content,
      selectedParagraphId,
      getParagraphCommentCount,
      onCommentIconClick,
    });
    const { bubbleRef, selectionBubble, selectionActions } = useSelectionShareBubble(containerRef);

    const nodes = useMemo(() => parseLexicalNodes(content), [content]);

    if (!nodes.length) {
      return <div className="text-sm text-[#6e7384] mb-3">Нет содержимого</div>;
    }

    return (
      <ParagraphCommentUiContext.Provider value={paragraphCommentUi}>
        <div className="relative">
          <div
            ref={containerRef}
            className={
              inheritTypography
                ? 'max-w-none break-words [overflow-wrap:anywhere]'
                : 'prose prose-lg max-w-none leading-relaxed'
            }
            style={{ color: textColor }}
          >
            {nodes.map((node, idx) => renderLexicalNode(node, idx, textColor))}
          </div>
          <SelectionShareBubble
            bubbleRef={bubbleRef}
            selectionBubble={selectionBubble}
            selectionActions={selectionActions}
          />
        </div>
      </ParagraphCommentUiContext.Provider>
    );
  },
);

LexicalContentRenderer.displayName = 'LexicalContentRenderer';
